// ─────────────────────────────────────────────────────────────────────────────
// Paleta ÚNICA del círculo de iniciales (avatar sin foto) de toda la aplicación.
//
// 🔴 Es la paleta corporativa pedida el 2026-09-03, y el ORDEN es parte del pedido: se recorre tal
// cual y se repite desde el principio al agotarse. Antes convivían dos criterios —un hash sobre
// `var(--cp1..6)` en `brillamosShared.tsx` y un morado fijo repetido en una docena de hojas de
// estilo—, así que la misma persona salía de un color distinto según la pantalla.
//
// DOS FORMAS DE ELEGIR, y cada una tiene su caso:
//   · `avatarColorByIndex` — cuando la persona ocupa una POSICIÓN en una lista (tabla de líderes,
//     chips de destinatarios). Ahí «en secuencia» significa literalmente eso.
//   · `avatarColorForSeed`  — cuando el avatar está SOLO (barra lateral, firma de un post-it). No hay
//     índice que seguir, así que el color se deriva del id de la persona: estable entre pantallas y
//     entre sesiones, que es lo que espera quien reconoce a alguien por su color.
// ─────────────────────────────────────────────────────────────────────────────
import type { CSSProperties } from 'react';
import { hexARgb, luminancia } from '@/utils/colorMix';

export const AVATAR_PALETTE = [
  '#F08521', '#342F82', '#E11F26', '#20B15A', '#EF5395', '#40B8E9', '#D8D8D8',
] as const;

/** Texto claro y texto oscuro del avatar. El oscuro es el gris de tinta ya usado en las tablas. */
const TEXTO_CLARO = '#FFFFFF';
const TEXTO_OSCURO = '#1F2937';

/**
 * Umbral de luminancia por encima del cual el fondo es demasiado claro para texto blanco.
 *
 * 🔴 Existe por `#D8D8D8`, el 7º color (luminancia 0.847): con texto blanco encima no se lee. El
 * criterio es el mismo que ya usa `fundamentalTintStyle` para decidir el color de texto sobre un
 * tinte, pero el número NO es 0.6: con 0.6, `#F08521` (luminancia 0.603) y `#40B8E9` (0.603) —el
 * naranja y el cian de la marca— caerían del lado del texto oscuro por tres milésimas, y esos dos
 * colores con tinta oscura no es lo pedido. Con 0.7 solo el gris queda con texto oscuro.
 */
const UMBRAL_TEXTO_OSCURO = 0.7;

/** Color por POSICIÓN en una lista. Cicla la paleta; un índice inválido cae al primer color. */
export function avatarColorByIndex(indice: number): string {
  if (!Number.isFinite(indice) || indice < 0) return AVATAR_PALETTE[0];
  return AVATAR_PALETTE[Math.floor(indice) % AVATAR_PALETTE.length];
}

/**
 * Color por SEMILLA (id de la persona; su nombre si no hay id). Determinista: hash multiplicativo
 * de 31, el mismo que usaba `avatarColorFor` en `brillamosShared.tsx`, para que nadie cambie de
 * color al migrar de una paleta a la otra más de lo estrictamente necesario.
 */
export function avatarColorForSeed(semilla: string | null | undefined): string {
  const texto = (semilla ?? '').trim();
  if (!texto) return AVATAR_PALETTE[0];

  let acumulado = 0;
  for (let posicion = 0; posicion < texto.length; posicion += 1) {
    acumulado = (acumulado * 31 + texto.charCodeAt(posicion)) >>> 0;
  }
  return AVATAR_PALETTE[acumulado % AVATAR_PALETTE.length];
}

/** Color de texto legible sobre `fondoHex`. Nunca se fija en blanco: ver `UMBRAL_TEXTO_OSCURO`. */
export function avatarTextColor(fondoHex: string): string {
  return luminancia(hexARgb(fondoHex)) > UMBRAL_TEXTO_OSCURO ? TEXTO_OSCURO : TEXTO_CLARO;
}

/**
 * Las dos custom properties que consume `.pmp-avatar` (ver `src/styles/avatar.css`).
 *
 * 🔴 Se devuelven como variables y no como `background`/`color` directos para que la declaración
 * viva en la hoja de estilos y solo el VALOR viaje por el `style` — el color depende de la persona,
 * así que no puede ser una clase estática.
 */
export function avatarVars(fondoHex: string): CSSProperties {
  return { '--avatar-bg': fondoHex, '--avatar-fg': avatarTextColor(fondoHex) } as CSSProperties;
}
